import { GenerateImageOptions, GenerateImageRendererOptions } from "@centricsoftware/model-viewer";
import JsonResponse from "csi/comm/JsonResponse";
import { action, makeObservable, observable } from "mobx";
import React from "react";
import SnapshotTemplateDialogStore from "../snapshotTemplates/dialogs/SnapshotTemplateDialogStore";
import { SnapshotTemplateId, SnapshotTemplateOptions } from "../snapshotTemplates/snapshotUtils";

export class LocalGenerateImageStore {
	readonly templateId: SnapshotTemplateId;

	constructor(snapshotTemplateId: SnapshotTemplateId) {
		makeObservable(this);
		this.templateId = snapshotTemplateId;
	}

	/** Determine if Save as New modal is open */
	@observable isSaveAsNewModalOpen: boolean = false;
    
	/** Image options */
	@observable.ref imageOptions: GenerateImageOptions = null;

	/** Renderer options */
	@observable.ref rendererOptions: GenerateImageRendererOptions = null;

	/** Template options */
	@observable.ref templateOptions: SnapshotTemplateOptions = null;

	/** User can save a template */
	@observable userCanSaveTemplate: boolean = false;

	/** response from new template save */
	@observable.ref dataToAdd: JsonResponse = null;

	/** Dialog store to pass to 'Save As New' dialog */
	get dialogStore(): SnapshotTemplateDialogStore {
		return new SnapshotTemplateDialogStore(this.closeModal, this.processDataHasChanged, null, this.templateId);
	};
    
	/** Set the renderer options */
	@action setRendererOptions = (rendererOptions: GenerateImageRendererOptions): void => {
		this.rendererOptions = rendererOptions;
	};

	/** Set the image Options */
	@action setImageOptions = (imageOptions: GenerateImageOptions): void => {
		this.imageOptions = imageOptions;
	};

	/** Set the template options */
	@action setTemplateOptions = (templateOptions: SnapshotTemplateOptions): void => {
		this.templateOptions = templateOptions;
	};

	/** Set if a user can save a template */
	@action setUserCanSaveTemplate = (canSave: boolean): void => {
		this.userCanSaveTemplate = canSave;
	};

	/** Set if data has changed from saving a new template */
	@action setDataHasChanged = (response: JsonResponse): void => {
		this.dataToAdd = response;
	};

	/** Set Save as New modal open */
	@action openModal = (): void => {
		this.isSaveAsNewModalOpen = true;
	};

	/** Set Save as New modal close */
	@action closeModal = (): void => {
		this.isSaveAsNewModalOpen = false;
	};

	/** process data has changed */
	@action processDataHasChanged = (response:JsonResponse): void => {
		this.closeModal();
		this.setDataHasChanged(response);
	};
}

export const LocalGenerateImageStoreContext = React.createContext<LocalGenerateImageStore>(undefined);
export const useLocalGenerateImageStore = ():LocalGenerateImageStore => React.useContext(LocalGenerateImageStoreContext);